import type {
  LocalizedText,
  TemplateField,
  TemplateFieldDateRange,
} from "./intake-template";
import {
  SYMPTOM_INTAKE_VALUES,
  SYMPTOM_QUESTIONS,
  SYMPTOM_REGISTER_V2,
  SYMPTOM_SEVERITY_VALUES,
} from "./symptom-entry";
import { TRANSFER_SYMPTOM_QUESTIONS } from "./transfer-symptom-fields";

// TES-1247 (Ella 2.1 slice A) — the canonical question register.
//
// Drive, "Ella 2.1 — Productdefinition, frågeregister, Besvärsguide och
// datakongruens" §3:
// "Varje fråga ska centralt definiera question_id, exakt patientcopy, svarsskala, tidsram, vilka ytor den får visas på, återanvändningsfönster, patientvisning, coachvisning, läkarvisning och eventuell medicinsk signalhantering."
//
// This file is that central definition. A form template field opts in with
// `TemplateField.questionId`; from then on its copy and answer scale are the
// register's (questionBindingViolations below), the API refuses a draft or
// publish that differs, and the admin editor locks the field.
//
// WHERE THE COPY COMES FROM. Drive §5 verbatim where it gives the question.
// Where Drive gives only the id (WEIGHT_KG, WAIST_CM, BLOOD_PRESSURE_PULSE,
// MONTH_LAST_DOSE_DATE, MONTH_ADHERENCE), the copy is the code seed's, which
// is what patients are answering. English is the seeds' (or symptom-entry's)
// where they exist. Where Drive and the seeds disagree, the comment on the
// question says so and says which side the register takes; the fields that
// still ask it differently are listed in the api's question-sources.ts and
// stay unbound until slice E (TES-1251) aligns them.
//
// The symptom questions are not repeated here: symptom-entry.ts (TES-1250)
// already holds Drive §5.4 verbatim and the canonical symptom list v2
// (Drive §6.1). The register reads them from there, so there is one copy.
//
// NORMALISED VALUES. Only where a series already normalises the answer
// (observation-extraction.ts, the symptom severity scale) is a number given,
// and it is the same number, so moving the normalisation into the register
// (slice B, TES-1248) changes no stored series. Everywhere else it is null:
// an ordinal nobody has decided would be invented data.
//
// VERSIONING. `version` is the question's own. Changing a question's copy or
// scale is a new version of it and needs new seed versions of every form that
// binds it, in the same change: a bound field always matches the current
// version, and a published template is never rewritten. Help text is held
// to the register the same way (help_differs) but is not part of the
// version, since no answer means anything else under new help (TES-1573):
// changing it keeps the version and still needs the new seed versions.
// TES-1654 — the same holds for an answer added for one surface only
// (QuestionScaleOption.surfaces) that leaves every other answer's value,
// label, number and order as they were: no answer given before means
// anything else, so the version stays, and a new version would leave every
// earlier answer out of the series (wellbeing-series sameAnswers).

export const QUESTION_REGISTER_VERSION = 1;

/** Where a question may be asked. Drive §4 names the surfaces. */
export const QUESTION_SURFACES = [
  "f01",
  "f02",
  "f04",
  "f05",
  "f06",
  "f07",
  "cp06",
  // A completion (komplettering) the doctor asked for.
  "completion",
  // The dose registration (CP-12 and the recurring one).
  "dose",
  "besvarsguide",
  // Direct registration on the "Din hälsa över tid" screen.
  "health",
  "hem",
] as const;
export type QuestionSurface = (typeof QUESTION_SURFACES)[number];

/** The period the question asks about. */
export const QUESTION_TIMEFRAMES = [
  "last_7_days",
  "now",
  "since_last_checkin",
  "since_last_monthly_review",
  // The reading itself: when it was measured.
  "at_measurement",
  // The answer is a date.
  "point_in_time",
  // Drive gives none and the copy names none.
  "not_stated",
] as const;
export type QuestionTimeframe = (typeof QUESTION_TIMEFRAMES)[number];

export interface QuestionScaleOption {
  value: string;
  label: LocalizedText;
  /** The answer's position on a numeric series; null when there is none. */
  normalized: number | null;
  /**
   * TES-1654 — the surfaces that offer this answer, when not all of the
   * question's surfaces do. Absent: every surface offers it. A field bound
   * to the question offers exactly the answers of one of its surfaces
   * (scaleDifference); registerBoundField gives a field the answers every
   * surface offers unless it names its surface.
   */
  surfaces?: readonly QuestionSurface[];
}

export type QuestionScale =
  | { kind: "single_select"; options: readonly QuestionScaleOption[] }
  | { kind: "multi_select"; options: readonly QuestionScaleOption[] }
  | {
      kind: "scale_1_5";
      /** Index 0 is value 1. The stored answer is the number itself. */
      steps: readonly {
        value: 1 | 2 | 3 | 4 | 5;
        label: LocalizedText;
        normalized: number;
      }[];
    }
  | {
      kind: "numeric";
      unit: string;
      min: number;
      max: number;
      integer?: boolean;
    }
  | {
      kind: "date";
      dateRange: TemplateFieldDateRange;
      /** Drive: "Datum eller ungefärligt datum". */
      approximateAllowed?: boolean;
    }
  | { kind: "free_text" };

/** One asked thing: its exact copy and scale. */
export interface QuestionPrompt {
  copy: LocalizedText;
  /**
   * TES-1533 (Ö-817) — the same question in a surface's decided words: same
   * codes, same scale, same series, only the wording fits the context. A
   * field bound to the question may ask `copy` or one of these.
   */
  contextCopy?: Partial<Record<QuestionSurface, LocalizedText>>;
  /**
   * Help text that belongs to the question wherever it is asked (a measuring
   * instruction). Absent: the help is the surface's own and is not compared.
   */
  help?: LocalizedText;
  scale: QuestionScale;
}

/** How an answer is handled medically, as the evaluators do it today. */
export interface QuestionSignalRule {
  surfaces: readonly QuestionSurface[];
  when:
    | { kind: "values"; values: readonly string[] }
    | { kind: "at_most"; value: number };
  /**
   * urgent_hard_stop: the acute path (112/1177) · doctor_review: a flag the
   * doctor reviews · safety_copy: the safety copy without a care-team case
   * (Ö-447) · flag_only: recorded, nobody is asked to act.
   */
  effect: "urgent_hard_stop" | "doctor_review" | "safety_copy" | "flag_only";
  /** The evaluator rule names that implement it. */
  rules: readonly string[];
}

export interface QuestionVisibility {
  patient: boolean;
  coach: boolean;
  doctor: boolean;
}

interface QuestionBase {
  questionId: string;
  version: number;
  timeframe: QuestionTimeframe;
  surfaces: readonly QuestionSurface[];
  /**
   * How many days an earlier answer may be shown again for her to confirm or
   * change (Drive §3). Never reused silently: confirming is an act of hers
   * (Ö-444). Null: never offered again; a new answer is always asked.
   */
  reuseWindowDays: number | null;
  visibility: QuestionVisibility;
  signals: readonly QuestionSignalRule[];
}

export interface SingleQuestion extends QuestionBase, QuestionPrompt {
  kind: "single";
}

/** One occasion asked as several fields (BLOOD_PRESSURE_PULSE). */
export interface CompositeQuestion extends QuestionBase {
  kind: "composite";
  parts: Readonly<Record<string, QuestionPrompt>>;
}

export type RegisterQuestion = SingleQuestion | CompositeQuestion;

const sv = (s: string, e: string): LocalizedText => ({ sv: s, en: e });

const opt = (
  value: string,
  label: LocalizedText,
  normalized: number | null = null,
): QuestionScaleOption => ({ value, label, normalized });

// Medical structured answers are the doctor's, never coach data. A-01:
// "Medicinska strukturerade svar, medicinska flaggor, följsamhet till ordination, dosavvikelser, symptomdetaljer och medicinskt förändrat driv ska inte visas som coachdata i veckolista, Patientbild, trend, arbetskort, patientminne eller AI-underlag."
const DOCTOR_ONLY: QuestionVisibility = {
  patient: true,
  coach: false,
  doctor: true,
};
// WELLBEING_7D and WEIGHT_KG are what the coach's trend shows today
// (clinician/checkin-trend.ts) and SHARED in the F-04 field classes.
const SHARED: QuestionVisibility = { patient: true, coach: true, doctor: true };

// The symptom questions are asked wherever she can register a symptom
// (symptom-entry.ts SYMPTOM_SOURCE_SURFACES). TES-1441: F-02 v18 asks them
// under its own current-symptoms question (no SYMPTOM_TREND there).
const SYMPTOM_SURFACES: readonly QuestionSurface[] = [
  "f02",
  "f04",
  "f05",
  "dose",
  "besvarsguide",
  "health",
];

// TES-1442 (Ö-786 assumption (a)) — F-07 v14–v16 ask which symptom, when it
// began and her note about her earlier treatment. From v17 (TES-1532) F-07
// asks the list and the onset in its own decided words (their f07
// contextCopy), whether she still has each symptom (SYMPTOM_STILL_PRESENT)
// and, on "Ja", the severity.
const F07_SYMPTOM_SURFACES: readonly QuestionSurface[] = [
  ...SYMPTOM_SURFACES,
  "f07",
];

const { SYMPTOM_SEVERITY, SYMPTOM_TREND, SYMPTOM_INTAKE } = SYMPTOM_QUESTIONS;

// The severity series both check-ins already write (observation-extraction
// SEVERITY_123).
const SEVERITY_NORMALIZED: Record<
  (typeof SYMPTOM_SEVERITY_VALUES)[number],
  number
> = { latt: 1, mattligt: 2, kraftigt: 3 };
// observation-extraction's intake_ability series.
const INTAKE_NORMALIZED: Record<
  (typeof SYMPTOM_INTAKE_VALUES)[number],
  number
> = { ja: 3, oftast: 2, ibland_svart: 1, ofta_svart: 0 };

export const QUESTION_REGISTER_V1 = [
  // §5.1. Drive: "WELLBEING_7D — Hur har du mått överlag den senaste veckan? Svar: 1 Mycket dåligt | 2 | 3 | 4 | 5 Mycket bra."
  // CONFLICT: Drive labels only the endpoints; F-04 (since v4) and F-05 v5
  // label all five steps, and F-05's Drive text asks for it "ordagrant med
  // samma skala" as F-04 asks it. The register takes the seeds' five labels,
  // which is what every answer so far was given against; dropping the middle
  // three is a new version of the question once Emelie decides it.
  {
    kind: "single",
    questionId: "WELLBEING_7D",
    version: 1,
    copy: sv(
      "Hur har du mått överlag den senaste veckan?",
      "How have you felt overall this past week?",
    ),
    scale: {
      kind: "scale_1_5",
      steps: [
        { value: 1, label: sv("Mycket dåligt", "Very poor"), normalized: 1 },
        { value: 2, label: sv("Dåligt", "Poor"), normalized: 2 },
        { value: 3, label: sv("Okej", "Okay"), normalized: 3 },
        { value: 4, label: sv("Bra", "Good"), normalized: 4 },
        { value: 5, label: sv("Mycket bra", "Very good"), normalized: 5 },
      ],
    },
    timeframe: "last_7_days",
    // Drive: the dose surface may use it only "när frågans sjudagarsperiod är
    // relevant och ett aktuellt svar saknas".
    surfaces: ["f04", "f05", "dose"],
    reuseWindowDays: 7,
    visibility: SHARED,
    signals: [
      {
        surfaces: ["f04"],
        when: { kind: "at_most", value: 2 },
        effect: "flag_only",
        rules: ["week_low_wellbeing"],
      },
    ],
  },
  // §5.2. Drive gives no question text, only that weight is one Measurement
  // wherever it comes from and that a fresh value is confirmed or changed
  // under the seven-day rule.
  // TES-1449 (Ö-712, Emelie 2026-10-19) — version 2:
  // "Den gemensamma viktfrågan är exakt ”Vad väger du? (kg)”. ”Hoppa över” är ett separat val endast där viktuppgiften är frivillig enligt respektive formulärs befintliga regler. Mätdatum visas och sparas separat från frågetexten och från registreringstidpunkten."
  // So "Hoppa över" and "idag" leave the copy: the skip is the card's own
  // choice, and the day is its own field. Version 1 was F-04's
  // "Vad väger du idag? (kg) — eller hoppa över denna vecka", bound by F-04
  // v6 and v7; F-05 asked "Nuvarande vikt (kg) — eller hoppa över", unbound.
  // F-04 v8 and F-05 v8 both bind version 2.
  // TES-1533 (Ö-809) — F-01 v12 and F-02 (intake v25) ask this copy too, but
  // unbound: their bounds are their own (F-01 30–350 kg, F-02 30–400 kg) and
  // the weight stays required there. f01-weight-v12.spec.ts (F-01)
  // and the api's intake-weight-symptoms-v20.spec.ts (F-02) hold the labels
  // to this copy.
  {
    kind: "single",
    questionId: "WEIGHT_KG",
    version: 2,
    copy: sv("Vad väger du? (kg)", "What do you weigh? (kg)"),
    scale: { kind: "numeric", unit: "kg", min: 1, max: 400 },
    timeframe: "at_measurement",
    surfaces: [
      "f01",
      "f02",
      "f04",
      "f05",
      "f06",
      "f07",
      "cp06",
      "hem",
      "health",
    ],
    reuseWindowDays: 7,
    visibility: SHARED,
    signals: [],
  },
  // §5.2. Drive: "WAIST_CM används i F-05 och frivillig direktregistrering. Samma mätinstruktion, enhet och mätserie används överallt."
  // No question text in Drive; F-05's label and its measuring instruction
  // (Emelie's CP-06 instruction, TES-849), which the register owns as help.
  {
    kind: "single",
    questionId: "WAIST_CM",
    version: 1,
    copy: sv("Midjemått (frivilligt)", "Waist (optional)"),
    help: sv(
      "Mät i höjd med naveln med måttbandet vågrätt och utan att dra åt.",
      "Measure at the level of your navel, with the tape horizontal and without pulling it tight.",
    ),
    // MEASUREMENT_BOUNDS.waist.
    scale: { kind: "numeric", unit: "cm", min: 40, max: 250 },
    timeframe: "at_measurement",
    surfaces: ["f05", "health"],
    reuseWindowDays: 7,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  // §5.2. Drive: "BLOOD_PRESSURE_PULSE är ett sammanhållet mättillfälle med systoliskt blodtryck, diastoliskt blodtryck, vilopuls, measured_at och provenance."
  // One question, four fields. The labels are F-05's (which reused F-07's
  // approved ones). F-05's help on the systolic field says the question is
  // optional there, so it is that surface's text and not the register's.
  {
    kind: "composite",
    questionId: "BLOOD_PRESSURE_PULSE",
    version: 1,
    parts: {
      systolic: {
        copy: sv("Övertryck (systoliskt)", "Systolic"),
        scale: {
          kind: "numeric",
          unit: "mmHg",
          min: 50,
          max: 300,
          integer: true,
        },
      },
      diastolic: {
        copy: sv("Undertryck (diastoliskt)", "Diastolic"),
        scale: {
          kind: "numeric",
          unit: "mmHg",
          min: 30,
          max: 200,
          integer: true,
        },
      },
      pulse: {
        copy: sv("Vilopuls", "Resting pulse"),
        scale: {
          kind: "numeric",
          unit: "slag/min",
          min: 25,
          max: 220,
          integer: true,
        },
      },
      // TES-1573 (Ö-858, Emelie 2026-10-22): "Hjälptext: ”Ange datumet då blodtrycket och pulsen mättes.”"
      // The app now shows today in the date, so "Lämna tomt om det var idag"
      // left the help. F-05 v13 binds this; F-05 v8–v12 asked the old help.
      // The version stays 1: the help is an instruction, not the answer's
      // scale, so no stored reading means anything else.
      measured_on: {
        copy: sv("Mätdatum", "Measurement date"),
        help: sv(
          "Ange datumet då blodtrycket och pulsen mättes.",
          "Enter the date your blood pressure and pulse were measured.",
        ),
        scale: { kind: "date", dateRange: "past" },
      },
    },
    timeframe: "at_measurement",
    // Drive: "Direktregistrering, F-05 och relevant komplettering ska använda samma objekt."
    surfaces: ["f05", "health", "completion", "f07"],
    reuseWindowDays: 7,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  // §5.3, all three verbatim in Drive and in the F-05 seed. Drive: "Dessa
  // frågor och skalor ägs av F-05." The timeframe is not in the copy; F-05
  // asks it once a month.
  {
    kind: "single",
    questionId: "MONTH_HUNGER",
    version: 1,
    copy: sv("Hur har hungern varit?", "How has your hunger been?"),
    // Not a monotonic scale: observation-extraction keeps it as text only.
    scale: {
      kind: "single_select",
      options: [
        opt("mycket_svag", sv("Mycket svag", "Very weak")),
        opt("lagom_stabil", sv("Lagom/stabil", "Balanced/stable")),
        opt("mer_an_onskat", sv("Mer än önskat", "More than I'd like")),
        opt("varierande", sv("Varierande", "Varying")),
        opt("svart_att_bedoma", sv("Svårt att bedöma", "Hard to say")),
      ],
    },
    timeframe: "since_last_monthly_review",
    surfaces: ["f05"],
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  // TES-1450 (Emelie 2026-10-19, Ö-714): "Varierar sparas som eget
  // kategoriskt svar utan siffervärde (inte noll eller medelvärde)."
  // Version 2: "Varierar" has no number. Copy, values, labels and order are
  // version 1's, so F-05's bound field is unchanged and needs no new seed.
  // The other numbers are kept so no stored point moves; they are positions
  // on the series, not distances ("inga lika stora numeriska intervall …
  // antas"). Version 1, under which "Varierar" was stored as 3, stays in
  // QUESTION_REGISTER_HISTORY for the rows answered on it.
  {
    kind: "single",
    questionId: "MONTH_SATIETY",
    version: 2,
    copy: sv(
      "Hur lätt blir du lagom mätt?",
      "How easily do you feel comfortably full?",
    ),
    scale: {
      kind: "single_select",
      options: [
        opt("mycket_latt", sv("Mycket lätt", "Very easily"), 1),
        opt("ganska_latt", sv("Ganska lätt", "Fairly easily"), 2),
        opt("svart", sv("Svårt", "With difficulty"), 4),
        opt("varierar", sv("Varierar", "It varies")),
      ],
    },
    timeframe: "since_last_monthly_review",
    surfaces: ["f05"],
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  {
    kind: "single",
    questionId: "MONTH_CRAVINGS",
    version: 1,
    copy: sv(
      "Hur mycket har sug eller mycket tankar på mat påverkat dig?",
      "How much have cravings or food thoughts affected you?",
    ),
    scale: {
      kind: "single_select",
      options: [
        opt("inte_alls", sv("Inte alls", "Not at all"), 0),
        opt("lite", sv("Lite", "A little"), 1),
        opt("en_del", sv("En del", "Somewhat"), 2),
        opt("mycket", sv("Mycket", "A lot"), 3),
      ],
    },
    timeframe: "since_last_monthly_review",
    surfaces: ["f05"],
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  // §5.4. Drive: "Denna gemensamma port ersätter separata veckovarianter och månadsvarianter i 2.1. F-04 och F-05 använder samma fråga."
  // Neither form asks it yet: F-04 has "Har du fått nya besvär eller har
  // något förändrats sedan förra veckan?" and F-05 "Har du haft besvär som
  // kan vara relevanta för behandlingen?". Same three answers in both.
  {
    kind: "single",
    questionId: "SYMPTOM_CHANGE_GATE",
    version: 1,
    copy: sv(
      "Har du haft något nytt eller pågående besvär, eller har något besvär förändrats sedan din senaste incheckning?",
      "Have you had any new or ongoing symptom, or has any symptom changed since your last check-in?",
    ),
    scale: {
      kind: "single_select",
      options: [
        opt("nej", sv("Nej", "No")),
        opt("ja", sv("Ja", "Yes")),
        opt("osaker", sv("Osäker", "Unsure")),
      ],
    },
    timeframe: "since_last_checkin",
    // Per Drive, the symptom guide ("Besvärsguiden") and the dose
    // registration open the symptom module directly, without the gate.
    surfaces: ["f04", "f05"],
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [
      {
        surfaces: ["f04", "f05"],
        when: { kind: "values", values: ["ja", "osaker"] },
        effect: "doctor_review",
        // On the forms' own gates, until slice E puts this one in their place.
        rules: ["week_symptoms_reported", "month_symptoms_reported"],
      },
    ],
  },
  // §5.4 SYMPTOM_TYPE to SYMPTOM_NOTE: symptom-entry.ts's questions and the
  // symptom list v2, read from there.
  {
    kind: "single",
    questionId: "SYMPTOM_TYPE",
    version: 1,
    copy: SYMPTOM_QUESTIONS.SYMPTOM_TYPE.label,
    // TES-1533 — Emelie 2026-10-21 (Ö-817, part 4): "Listfrågan i F-02 är ”Vilka besvär har du just nu?” Direktregistrering under Din hälsa behåller ”Vilket besvär vill du registrera?” Samma koder och skalor där samma uppgift mäts."
    // The English is ours.
    contextCopy: {
      f02: sv(
        "Vilka besvär har du just nu?",
        "Which symptoms do you have right now?",
      ),
      // TES-1532 — Emelie 2026-10-21 (Ö-813): "F-07 får kontextanpassad text: ”Vilka besvär har du haft under din tidigare behandling?” […] Samma besvärskoder och historik används."
      f07: TRANSFER_SYMPTOM_QUESTIONS.list.label,
    },
    // Drive: "Flerval från det kanoniska symtomregistret."
    scale: {
      kind: "multi_select",
      options: SYMPTOM_REGISTER_V2.map((s) => opt(s.code, s.label)),
    },
    timeframe: "not_stated",
    surfaces: F07_SYMPTOM_SURFACES,
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  {
    kind: "single",
    questionId: "SYMPTOM_SEVERITY",
    version: 1,
    copy: SYMPTOM_SEVERITY.label,
    scale: {
      kind: "single_select",
      options: SYMPTOM_SEVERITY.options.map((o) =>
        opt(o.value, o.label, SEVERITY_NORMALIZED[o.value]),
      ),
    },
    timeframe: "now",
    // TES-1532 (Ö-786) — and F-07 from v17, on "Har du fortfarande det här
    // besväret?" = "Ja". No signal there: F-07's risk codes are unchanged.
    surfaces: F07_SYMPTOM_SURFACES,
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [
      {
        surfaces: ["f04", "f05"],
        when: { kind: "values", values: ["kraftigt"] },
        effect: "doctor_review",
        rules: ["week_severe_symptom", "month_severe_symptom_<code>"],
      },
      {
        surfaces: ["dose", "besvarsguide", "health"],
        when: { kind: "values", values: ["kraftigt"] },
        effect: "safety_copy",
        rules: ["symptom_severe"],
      },
    ],
  },
  {
    kind: "single",
    questionId: "SYMPTOM_TREND",
    version: 1,
    copy: SYMPTOM_TREND.label,
    // Categorical: "Nytt" and "Kommit tillbaka" are not points on a line.
    scale: {
      kind: "single_select",
      options: SYMPTOM_TREND.options.map((o) => opt(o.value, o.label)),
    },
    timeframe: "not_stated",
    surfaces: SYMPTOM_SURFACES,
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  {
    kind: "single",
    questionId: "SYMPTOM_STARTED_AT",
    version: 1,
    copy: SYMPTOM_QUESTIONS.SYMPTOM_STARTED_AT.label,
    // TES-1532 — Ö-813: "”När började besväret?” […] Historisk debut blandas inte med senare förändring i ett otydligt datumfält."
    // In F-07 the date is her symptom's onset in her earlier treatment only.
    contextCopy: { f07: TRANSFER_SYMPTOM_QUESTIONS.onset.label },
    scale: { kind: "date", dateRange: "past", approximateAllowed: true },
    timeframe: "point_in_time",
    surfaces: F07_SYMPTOM_SURFACES,
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  {
    kind: "single",
    questionId: "SYMPTOM_INTAKE",
    version: 1,
    copy: SYMPTOM_INTAKE.label,
    scale: {
      kind: "single_select",
      options: SYMPTOM_INTAKE.options.map((o) =>
        opt(o.value, o.label, INTAKE_NORMALIZED[o.value]),
      ),
    },
    timeframe: "not_stated",
    surfaces: SYMPTOM_SURFACES,
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    // The same answer is handled differently by surface today: F-05 sends
    // "Ofta svårt" down the acute path, a direct registration shows the
    // safety copy (symptom-entry, Ö-447).
    signals: [
      {
        surfaces: ["f05"],
        when: { kind: "values", values: ["ofta_svart"] },
        effect: "urgent_hard_stop",
        rules: ["month_intake_often_difficult"],
      },
      {
        surfaces: ["f05"],
        when: { kind: "values", values: ["ibland_svart"] },
        effect: "doctor_review",
        rules: ["month_intake_sometimes_difficult"],
      },
      {
        surfaces: ["dose", "besvarsguide", "health"],
        when: { kind: "values", values: ["ofta_svart"] },
        effect: "safety_copy",
        rules: ["symptom_intake_often_hard"],
      },
    ],
  },
  {
    kind: "single",
    questionId: "SYMPTOM_NOTE",
    version: 1,
    copy: SYMPTOM_QUESTIONS.SYMPTOM_NOTE.label,
    // Drive: "Fritext visas som kontext men är inte en grafbar datapunkt."
    scale: { kind: "free_text" },
    timeframe: "not_stated",
    surfaces: F07_SYMPTOM_SURFACES,
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  // TES-1532 — F-07 v17, Emelie 2026-10-21. Ö-813: "Därefter den redan godkända frågan ”Har du fortfarande det här besväret?”"
  // Ö-786: "Vid Ja ställs den gemensamma frågan om aktuell svårighetsgrad […] Vid Nej bevaras historik utan aktuell grafpunkt."
  // Its own answer is no point of any series; "Ja" asks SYMPTOM_SEVERITY,
  // which is.
  {
    kind: "single",
    questionId: "SYMPTOM_STILL_PRESENT",
    version: 1,
    copy: TRANSFER_SYMPTOM_QUESTIONS.still.label,
    scale: {
      kind: "single_select",
      options: TRANSFER_SYMPTOM_QUESTIONS.still.options.map((o) =>
        opt(o.value, o.label),
      ),
    },
    timeframe: "now",
    surfaces: ["f07"],
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  // §5.5. Drive: "DRIVE_MOOD_CHANGE — Har du märkt någon tydlig förändring i driv, glädje, initiativ eller lust att göra saker sedan förra incheckningen?"
  // and "F-05 använder samma question_id, copy och svarsskala. Endast tidsreferensen får bindas till senaste incheckning; en separat månadsvariant får inte skapa en ny serie."
  // The register takes Drive's text. Neither form asks it verbatim: F-04
  // says "en tydlig förändring", F-05 says "sedan förra månadsavstämningen"
  // (which F-05's own Drive document gives). The options are the same in
  // both and here.
  {
    kind: "single",
    questionId: "DRIVE_MOOD_CHANGE",
    version: 1,
    copy: sv(
      "Har du märkt någon tydlig förändring i driv, glädje, initiativ eller lust att göra saker sedan förra incheckningen?",
      "Have you noticed any clear change in drive, joy, initiative or desire to do things since your last check-in?",
    ),
    // observation-extraction's drive_mood series.
    scale: {
      kind: "single_select",
      options: [
        opt("nej", sv("Nej", "No"), 0),
        // TES-1654 — F-05 only. Drive, F-05 (2026-11-01): "Emelie har godkänt att enbart lägga till svarsalternativet ”Ja, det har blivit bättre” i månadsfrågan om driv, glädje, initiativ och lust. […] Det nya förbättringssvaret ska inte aktivera de försämringsinriktade följdfrågorna genom en generell regel om annat svar än Nej."
        // Drive's order puts it second. It has no number: the series counts
        // how much worse she feels from 0 ("Nej") up, and an improvement is
        // not a step on that scale (the doctor's chart draws it apart, see
        // health-over-time.ts). The English is ours.
        {
          ...opt(
            "battre",
            sv("Ja, det har blivit bättre", "Yes, it has got better"),
          ),
          surfaces: ["f05"],
        },
        opt(
          "lite_hanterbart",
          sv("Lite, men hanterbart", "A little, but manageable"),
          1,
        ),
        opt(
          "ja_paverkar_vardag",
          sv("Ja, det påverkar min vardag", "Yes, it affects my everyday life"),
          2,
        ),
        opt(
          "ja_avstangd_flat",
          sv(
            "Ja, jag känner mig ovanligt avstängd, flat eller orolig för det",
            "Yes, I feel unusually shut off, flat or worried about it",
          ),
          3,
        ),
      ],
    },
    timeframe: "since_last_checkin",
    surfaces: ["f04", "f05"],
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    // F-05 has no rule on it yet: its Drive document holds the follow-ups
    // until they have "versionssatt godkännande av medicinskt ansvarig".
    signals: [
      {
        surfaces: ["f04"],
        when: {
          kind: "values",
          values: ["ja_paverkar_vardag", "ja_avstangd_flat"],
        },
        effect: "doctor_review",
        rules: ["week_drive_mood_change"],
      },
      {
        surfaces: ["f04"],
        when: { kind: "values", values: ["lite_hanterbart"] },
        effect: "flag_only",
        rules: ["week_drive_mild"],
      },
    ],
  },
  // §5.6. Drive names the ids and says they are asked "endast när dosloggen
  // saknar tillräcklig information" (slice J); the copy is F-05's.
  {
    kind: "single",
    questionId: "MONTH_LAST_DOSE_DATE",
    version: 1,
    copy: sv("När tog du senast din dos?", "When did you last take your dose?"),
    scale: { kind: "date", dateRange: "past" },
    timeframe: "point_in_time",
    surfaces: ["f05"],
    reuseWindowDays: null,
    // Dose data is the doctor's (Ö-423).
    visibility: DOCTOR_ONLY,
    signals: [],
  },
  {
    kind: "single",
    questionId: "MONTH_ADHERENCE",
    version: 1,
    copy: sv(
      "Hur många ordinerade doser har du tagit sedan förra månadsavstämningen?",
      "How many prescribed doses have you taken since the last monthly review?",
    ),
    scale: {
      kind: "single_select",
      options: [
        opt("alla", sv("Alla", "All of them")),
        opt("missat_en", sv("Missat en", "Missed one")),
        opt("missat_flera", sv("Missat flera", "Missed several")),
        opt("osaker", sv("Osäker", "Unsure")),
        opt("inte_aktuellt", sv("Inte aktuellt", "Not applicable")),
      ],
    },
    timeframe: "since_last_monthly_review",
    surfaces: ["f05"],
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [
      {
        surfaces: ["f05"],
        when: { kind: "values", values: ["missat_flera", "osaker"] },
        effect: "doctor_review",
        rules: ["month_adherence_risk"],
      },
    ],
  },
] as const satisfies readonly RegisterQuestion[];

export type QuestionId = (typeof QUESTION_REGISTER_V1)[number]["questionId"];

/**
 * Earlier versions of a question, exactly as they were, for the answers given
 * on them (Observation.registerVersion). Never asked again.
 */
export const QUESTION_REGISTER_HISTORY: readonly RegisterQuestion[] = [
  // TES-1450 — MONTH_SATIETY before Ö-714: observation-extraction's numbers,
  // which put "Varierar" (3) before "Svårt" (4), not the order she sees and
  // not an ordinal. Rows answered on it keep their stored 3; a reader takes
  // the answer's position from the current version instead.
  {
    kind: "single",
    questionId: "MONTH_SATIETY",
    version: 1,
    copy: sv(
      "Hur lätt blir du lagom mätt?",
      "How easily do you feel comfortably full?",
    ),
    scale: {
      kind: "single_select",
      options: [
        opt("mycket_latt", sv("Mycket lätt", "Very easily"), 1),
        opt("ganska_latt", sv("Ganska lätt", "Fairly easily"), 2),
        opt("svart", sv("Svårt", "With difficulty"), 4),
        opt("varierar", sv("Varierar", "It varies"), 3),
      ],
    },
    timeframe: "since_last_monthly_review",
    surfaces: ["f05"],
    reuseWindowDays: null,
    visibility: DOCTOR_ONLY,
    signals: [],
  },
];

/** A question as it was at `version`: the current one or an earlier one. */
export function registerQuestionAt(
  id: string,
  version: number,
): RegisterQuestion | null {
  const current = registerQuestion(id);
  if (current?.version === version) return current;
  return (
    QUESTION_REGISTER_HISTORY.find(
      (q) => q.questionId === id && q.version === version,
    ) ?? null
  );
}

// §5.7. Drive: "PROG_ENERGY, PROG_FUNCTION, PROG_STRENGTH och PROG_PLAN_FIT är separata långsiktiga 12-veckorsmått och ska inte blandas ihop med WELLBEING_7D."
// F-06 does not exist yet, so neither does their copy. The ids are taken so
// nothing else uses them, and no field may bind to them until they are
// defined here.
export const RESERVED_QUESTION_IDS = [
  "PROG_ENERGY",
  "PROG_FUNCTION",
  "PROG_STRENGTH",
  "PROG_PLAN_FIT",
] as const;
export type ReservedQuestionId = (typeof RESERVED_QUESTION_IDS)[number];

export function registerQuestion(id: string): RegisterQuestion | null {
  return (
    (QUESTION_REGISTER_V1 as readonly RegisterQuestion[]).find(
      (q) => q.questionId === id,
    ) ?? null
  );
}

/** The prompt a bound field must match, or null when the binding is invalid. */
export function registerPromptFor(
  questionId: string,
  part?: string,
): QuestionPrompt | null {
  const q = registerQuestion(questionId);
  if (!q) return null;
  if (q.kind === "single") return part === undefined ? q : null;
  return part === undefined ? null : (q.parts[part] ?? null);
}

// ---------------------------------------------------------------------------
// Normalising an answer (TES-1248, slice B).

/**
 * The answer's position on the question's series, read from the register's
 * scale: an option's or a step's `normalized`, a numeric answer as itself.
 * Null when the scale gives no number for it (a text-only or categorical
 * question, a value the scale does not have, a date or free text). This is
 * the one place a wellbeing Observation's `valueNumeric` comes from, so a
 * series and the question that feeds it cannot disagree about the scale.
 */
export function registerNormalizedValue(
  questionId: string,
  answer: unknown,
  part?: string,
): number | null {
  const prompt = registerPromptFor(questionId, part);
  if (!prompt) return null;
  const scale = prompt.scale;
  switch (scale.kind) {
    case "single_select":
      return typeof answer === "string"
        ? (scale.options.find((o) => o.value === answer)?.normalized ?? null)
        : null;
    case "scale_1_5":
      return typeof answer === "number"
        ? (scale.steps.find((s) => s.value === answer)?.normalized ?? null)
        : null;
    case "numeric":
      return typeof answer === "number" && Number.isFinite(answer)
        ? answer
        : null;
    case "multi_select":
    case "date":
    case "free_text":
      return null;
  }
}

// ---------------------------------------------------------------------------
// Binding a template field.

export type QuestionBindingViolationCode =
  | "unknown_question"
  | "reserved_question"
  | "part_mismatch"
  | "kind_differs"
  | "copy_differs"
  | "help_differs"
  | "scale_differs"
  | "binding_removed";

export interface QuestionBindingViolation {
  code: QuestionBindingViolationCode;
  key: string;
  questionId: string;
  detail: string;
}

const sameText = (a: LocalizedText | undefined, b: LocalizedText | undefined) =>
  a?.sv === b?.sv && a?.en === b?.en;

/**
 * TES-1654 — the answers a surface offers: every answer without
 * `surfaces`, and those that name it. Without a surface, only the answers
 * every surface offers.
 */
export function optionsOffered(
  options: readonly QuestionScaleOption[],
  surface?: QuestionSurface,
): QuestionScaleOption[] {
  return options.filter(
    (o) =>
      o.surfaces === undefined ||
      (surface !== undefined && o.surfaces.includes(surface)),
  );
}

function optionsDifference(
  options: readonly { value: string; label: LocalizedText }[],
  offered: readonly QuestionScaleOption[],
): string | null {
  if (options.length !== offered.length) {
    return `${options.length} options, the register has ${offered.length}`;
  }
  for (const [i, o] of offered.entries()) {
    const f = options[i]!;
    if (f.value !== o.value) {
      return `option ${i + 1} is "${f.value}", the register has "${o.value}"`;
    }
    if (!sameText(f.label, o.label)) {
      return `option "${o.value}" is labelled "${f.label.sv}" / "${f.label.en}", the register has "${o.label.sv}" / "${o.label.en}"`;
    }
  }
  return null;
}

/** Why the field's scale is not the prompt's, or null when it is. */
function scaleDifference(
  field: TemplateField,
  scale: QuestionScale,
  surfaces: readonly QuestionSurface[],
): string | null {
  switch (scale.kind) {
    case "single_select":
    case "multi_select": {
      // A field matches the answers one of the question's surfaces offers.
      // The field does not say which surface it is on, so any will do, as
      // for contextCopy.
      const options = field.options ?? [];
      const differences = [
        optionsDifference(options, optionsOffered(scale.options)),
        ...surfaces.map((s) =>
          optionsDifference(options, optionsOffered(scale.options, s)),
        ),
      ];
      return differences.includes(null) ? null : differences[0]!;
    }
    case "scale_1_5": {
      const steps = field.stepLabels ?? [];
      if (steps.length !== scale.steps.length) return "step labels differ";
      for (const [i, s] of scale.steps.entries()) {
        if (!sameText(steps[i], s.label)) {
          return `step ${s.value} is labelled "${steps[i]!.sv}", the register has "${s.label.sv}"`;
        }
      }
      return null;
    }
    case "numeric": {
      const n = field.numeric ?? {};
      if (field.unit !== scale.unit) {
        return `unit "${field.unit ?? ""}", the register has "${scale.unit}"`;
      }
      if (
        n.min !== scale.min ||
        n.max !== scale.max ||
        (n.integer ?? false) !== (scale.integer ?? false)
      ) {
        return `bounds ${n.min}–${n.max}${n.integer ? " integer" : ""}, the register has ${scale.min}–${scale.max}${scale.integer ? " integer" : ""}`;
      }
      return null;
    }
    case "date":
      // A date field without a range is "past" (TemplateField.dateRange).
      return (field.dateRange ?? "past") === scale.dateRange
        ? null
        : `date range "${field.dateRange ?? "past"}", the register has "${scale.dateRange}"`;
    case "free_text":
      return null;
  }
}

const KINDS_FOR_SCALE: Record<QuestionScale["kind"], readonly string[]> = {
  single_select: ["single_select"],
  multi_select: ["multi_select"],
  scale_1_5: ["scale_1_5"],
  numeric: ["numeric"],
  date: ["date"],
  free_text: ["short_text", "long_text"],
};

/** The violations of one field; empty when it is unbound or matches. */
export function fieldBindingViolations(
  field: TemplateField,
): QuestionBindingViolation[] {
  const questionId = field.questionId;
  if (questionId === undefined) {
    return field.questionPart === undefined
      ? []
      : [
          {
            code: "part_mismatch",
            key: field.key,
            questionId: "",
            detail: `field "${field.key}" names a question part but no question`,
          },
        ];
  }
  const v = (code: QuestionBindingViolationCode, detail: string) => [
    { code, key: field.key, questionId, detail },
  ];
  if ((RESERVED_QUESTION_IDS as readonly string[]).includes(questionId)) {
    return v(
      "reserved_question",
      `${questionId} is reserved and has no definition yet`,
    );
  }
  if (!registerQuestion(questionId)) {
    return v("unknown_question", `${questionId} is not in the register`);
  }
  const prompt = registerPromptFor(questionId, field.questionPart);
  if (!prompt) {
    return v(
      "part_mismatch",
      field.questionPart === undefined
        ? `${questionId} is asked in parts; the field names none`
        : `${questionId} has no part "${field.questionPart}"`,
    );
  }
  if (!KINDS_FOR_SCALE[prompt.scale.kind].includes(field.kind)) {
    return v(
      "kind_differs",
      `field "${field.key}" is ${field.kind}, ${questionId} is ${prompt.scale.kind}`,
    );
  }
  const out: QuestionBindingViolation[] = [];
  const asked = [prompt.copy, ...Object.values(prompt.contextCopy ?? {})];
  if (!asked.some((copy) => sameText(field.label, copy))) {
    out.push(
      ...v(
        "copy_differs",
        `field "${field.key}" asks "${field.label.sv}" / "${field.label.en}", ${questionId} is "${prompt.copy.sv}" / "${prompt.copy.en}"`,
      ),
    );
  }
  if (prompt.help !== undefined && !sameText(field.help, prompt.help)) {
    out.push(
      ...v(
        "help_differs",
        `field "${field.key}" has other help text than ${questionId}`,
      ),
    );
  }
  const scale = scaleDifference(
    field,
    prompt.scale,
    registerQuestion(questionId)!.surfaces,
  );
  if (scale !== null) {
    out.push(...v("scale_differs", `field "${field.key}": ${scale}`));
  }
  return out;
}

type FieldsOf = { sections: readonly { fields: readonly TemplateField[] }[] };

/**
 * Every binding violation in a template. With `published`, a field that is
 * bound there must stay bound to the same question under the same key: the
 * lock is not something a draft can lift by dropping the id.
 */
export function questionBindingViolations(
  content: FieldsOf,
  published?: FieldsOf,
): QuestionBindingViolation[] {
  const fields = content.sections.flatMap((s) => s.fields);
  const out = fields.flatMap(fieldBindingViolations);
  if (published) {
    const byKey = new Map(fields.map((f) => [f.key, f]));
    for (const p of published.sections.flatMap((s) => s.fields)) {
      if (p.questionId === undefined) continue;
      const f = byKey.get(p.key);
      if (
        f &&
        (f.questionId !== p.questionId || f.questionPart !== p.questionPart)
      ) {
        out.push({
          code: "binding_removed",
          key: p.key,
          questionId: p.questionId,
          detail: `field "${p.key}" is bound to ${p.questionId}${p.questionPart ? `.${p.questionPart}` : ""} in the published version`,
        });
      }
    }
  }
  return out;
}
